import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { 
  Pencil, 
  Trash2, 
  CheckCircle, 
  Settings, 
  Globe, 
  Shield, 
  Database, 
  Layout, 
  RefreshCcw, 
  Plus, 
  Building2, 
  Search, 
  Check,
  Share2,
  Key,
  Copy
} from 'lucide-react';
import { Header } from '../components/Header';
import { SystemBackup } from '../components/SystemBackup';
import { clearSettingsCache } from '../hooks/useSettings';
import { EmailTemplatesManager } from '../components/EmailTemplatesManager';
import { MetaTemplatesManager } from '../components/MetaTemplatesManager';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

interface Tenant {
  id: string;
  slug: string;
  name: string;
  domain?: string;
  active: boolean;
  allowedProviders?: string[]; // ['WAHA', 'EVOLUTION', 'QUEPASA', 'EVOGO']
  apiToken?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    contacts: number;
    campaigns: number;
    whatsappSessions: number;
  };
  quota?: {
    maxUsers: number;
    maxContacts: number;
    maxCampaigns: number;
    maxConnections: number;
  };
}

interface User {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  ultimoLogin?: string | null;
  criadoEm: string;
  atualizadoEm: string;
  tenant?: {
    id: string;
    name: string;
    slug: string;
    active: boolean;
  } | null;
}

interface TenantFormData {
  name: string;
  adminUser: {
    nome: string;
    email: string;
    senha: string;
  };
  quotas: {
    maxUsers: string;
    maxContacts: string;
    maxCampaigns: string;
    maxConnections: string;
  };
  allowedProviders: string[]; // ['WAHA', 'EVOLUTION', 'QUEPASA', 'EVOGO']
}

interface UserFormData {
  nome: string;
  email: string;
  senha: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'USER';
  tenantId?: string; // Mantido para compatibilidade
  tenantIds?: string[]; // Novo: múltiplos tenants
  ativo: boolean;
}

const TENANT_PROVIDER_OPTIONS = [
  {
    value: 'WAHA',
    label: 'Waha',
    icon: '/iconewaha.png',
    description: 'WhatsApp HTTP API',
  },
  {
    value: 'EVOLUTION',
    label: 'Evolution API',
    icon: '/iconeevolutionapi.png',
    description: 'API Evolution',
  },
  {
    value: 'QUEPASA',
    label: 'Quepasa',
    icon: '/iconequepasa.png',
    description: 'Gateway Quepasa',
  },
  {
    value: 'EVOGO',
    label: 'Evogo',
    icon: '/iconeevogo.svg',
    description: 'API Evogo',
  },
] as const;

const DEFAULT_TENANT_PROVIDERS = TENANT_PROVIDER_OPTIONS.map((option) => option.value);






interface Settings {
  id: string;
  wahaHost: string;
  wahaApiKey: string;
  evolutionHost: string;
  evolutionApiKey: string;
  evogoUrlBase?: string;
  evogoApiKeyGlobal?: string;
  webshareApiKey?: string;
  quepasaUrl?: string;
  quepasaLogin?: string;
  quepasaPassword?: string;
  whatsappAppId?: string;
  whatsappAppSecret?: string;
  whatsappConfigurationId?: string;
  whatsappApiVersion?: string;
  whatsappSystemUserToken?: string;
  whatsappBusinessId?: string;
  whatsappWabaId?: string;
  logoUrl?: string;
  companyName?: string;
  faviconUrl?: string;
  pageTitle?: string;
  iconUrl?: string;
  openaiApiKey?: string;
  groqApiKey?: string;
}

const settingsSchema = z.object({
  wahaHost: z.string().refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'Host deve ser uma URL válida ou vazio'
  }),
  wahaApiKey: z.string().refine((val) => !val || val.length >= 10, {
    message: 'API Key deve ter pelo menos 10 caracteres ou estar vazia'
  }),
  evolutionHost: z.string().refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'Host deve ser uma URL válida ou vazio'
  }),
  evolutionApiKey: z.string().refine((val) => !val || val.length >= 10, {
    message: 'API Key deve ter pelo menos 10 caracteres ou estar vazia'
  }),
  evogoUrlBase: z.string().refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'URL Base deve ser uma URL válida ou vazio'
  }),
  evogoApiKeyGlobal: z.string().refine((val) => !val || val.length >= 10, {
    message: 'API Key Global deve ter pelo menos 10 caracteres ou estar vazia'
  }),
  webshareApiKey: z.string().refine((val) => !val || val.length >= 10, {
    message: 'API Key da Webshare deve ter pelo menos 10 caracteres ou estar vazia'
  }),
  webshareRotateDays: z.union([z.number(), z.string(), z.null()]).optional(),
  webshareRotateOnCampaign: z.boolean().optional(),
  webshareRotateMessages: z.union([z.number(), z.string(), z.null()]).optional(),
  quepasaUrl: z.string().refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'URL deve ser uma URL válida ou vazio'
  }),
  quepasaLogin: z.string().optional(),
  quepasaPassword: z.string().optional(),
  whatsappAppId: z.string().refine((val) => !val || val.length >= 5, {
    message: 'App ID deve ter pelo menos 5 caracteres ou estar vazio'
  }),
  whatsappAppSecret: z.string().refine((val) => !val || val.length >= 10, {
    message: 'App Secret deve ter pelo menos 10 caracteres ou estar vazio'
  }),
  whatsappConfigurationId: z.string().refine((val) => !val || val.length >= 5, {
    message: 'Configuration ID deve ter pelo menos 5 caracteres ou estar vazio'
  }),
  whatsappApiVersion: z.string().refine((val) => !val || /^v?\d+(\.\d+)?$/.test(val), {
    message: 'Versão da API deve estar no formato v19.0 ou estar vazia'
  }),
  whatsappSystemUserToken: z.string().refine((val) => !val || val.length >= 10, {
    message: 'System User Token deve ter pelo menos 10 caracteres ou estar vazio'
  }),
  whatsappBusinessId: z.string().refine((val) => !val || val.length >= 5, {
    message: 'Business ID deve ter pelo menos 5 caracteres ou estar vazio'
  }),
  whatsappWabaId: z.string().refine((val) => !val || val.length >= 5, {
    message: 'WABA ID deve ter pelo menos 5 caracteres ou estar vazio'
  }),
});

const generalSettingsSchema = z.object({
  companyName: z.string().refine((val) => !val || (val.length >= 1 && val.length <= 100), {
    message: 'Nome da empresa deve ter entre 1 e 100 caracteres ou estar vazio'
  }),
  pageTitle: z.string().refine((val) => !val || (val.length >= 1 && val.length <= 100), {
    message: 'Título da página deve ter entre 1 e 100 caracteres ou estar vazio'
  }),
});

const createTemplateSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').regex(/^[a-z0-9_]+$/, 'Nome deve conter apenas letras minúsculas, números e sublinhados'),
  category: z.enum(['MARKETING', 'UTILITY', 'AUTHENTICATION']),
  language: z.string().min(2, 'Idioma é obrigatório'),
  body: z.string().min(1, 'Corpo da mensagem é obrigatório'),
});

type SettingsFormData = z.infer<typeof settingsSchema>;
type GeneralSettingsFormData = z.infer<typeof generalSettingsSchema>;
type CreateTemplateFormData = z.infer<typeof createTemplateSchema>;


interface MetaBusinessPortfolio {
  id: string;
  name: string;
  link?: string;
}

interface MetaWaba {
  id: string;
  name: string;
  businessId: string;
  currency?: string;
  timezone?: string;
  phoneNumbers?: Array<{
    id: string;
    displayPhoneNumber: string;
    verifiedName?: string;
  }>;
}

interface WabaShare {
  id: string;
  tenantId: string;
  tenant: {
    name: string;
  };
}

export function SuperAdminManagerPage() {
  const [activeTab, setActiveTab] = useState<'general' | 'integrations' | 'metaTemplates' | 'tenants' | 'users' | 'backup' | 'email'>('general');
  const [activeCompanySubTab, setActiveCompanySubTab] = useState<'dashboard' | 'list' | 'users'>('dashboard');
  const [activeMetaSubTab, setActiveMetaSubTab] = useState<'business' | 'wabas' | 'templates' | 'settings'>('business');
  const [metaBusinesses, setMetaBusinesses] = useState<MetaBusinessPortfolio[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');
  const [metaWabas, setMetaWabas] = useState<{ owned: MetaWaba[], client: MetaWaba[], all: MetaWaba[] }>({ owned: [], client: [], all: [] });
  const [selectedWaba, setSelectedWaba] = useState<MetaWaba | null>(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [isCreateTemplateModalOpen, setIsCreateTemplateModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedWabaToShare, setSelectedWabaToShare] = useState<MetaWaba | null>(null);
  const [wabaShares, setWabaShares] = useState<WabaShare[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isTokenDialogOpen, setIsTokenDialogOpen] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string>('');
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    adminUser: {
      nome: '',
      email: '',
      senha: ''
    },
    quotas: {
      maxUsers: '10',
      maxContacts: '1000',
      maxCampaigns: '50',
      maxConnections: '5'
    },
    allowedProviders: [...DEFAULT_TENANT_PROVIDERS]
  });
  const [userFormData, setUserFormData] = useState<UserFormData>({
    nome: '',
    email: '',
    senha: '',
    role: 'USER',
    tenantId: '',
    tenantIds: [],
    ativo: true
  });
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    type: 'info'
  });

  const showConfirm = (title: string, message: string, onConfirm: () => void, type: 'danger' | 'info' = 'info') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };

  const [activeModal, setActiveModal] = useState<'waha' | 'evolution' | 'evogo' | 'quepasa' | null>(null);
  const [, setIntegrationSettings] = useState<Settings | null>(null);
  const [activeIntegrationSubTab, setActiveIntegrationSubTab] = useState<'providers' | 'webshare' | 'evocrm'>('providers');

  // General settings states
  const [generalSettings, setGeneralSettings] = useState<Settings | null>(null);

  // Logo states
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Favicon states
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string>('');
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const {
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
  });

  // General settings form
  const {
    register: registerGeneral,
    handleSubmit: handleGeneralSubmit,
    setValue: setGeneralValue,
    formState: { errors: generalErrors, isSubmitting: isGeneralSubmitting },
  } = useForm<GeneralSettingsFormData>({
    resolver: zodResolver(generalSettingsSchema),
  });

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    let selectedTenantId = localStorage.getItem('selected_tenant_id');

    if (!selectedTenantId && token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.tenantId) {
          selectedTenantId = payload.tenantId;
        }
      } catch (e) {
        void e;
      }
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Helper para uploads (sem Content-Type automatico)
  const authenticatedUpload = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    let selectedTenantId = localStorage.getItem('selected_tenant_id');

    if (!selectedTenantId && token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.tenantId) {
          selectedTenantId = payload.tenantId;
        }
      } catch (e) {
        void e;
      }
    }

    const headers: HeadersInit = {
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const {
    register: registerCreateTemplate,
    handleSubmit: handleCreateTemplateSubmit,
    reset: resetCreateTemplate,
    formState: { errors: createTemplateErrors, isSubmitting: isCreatingTemplate }
  } = useForm<CreateTemplateFormData>({
    resolver: zodResolver(createTemplateSchema),
    defaultValues: {
      category: 'MARKETING',
      language: 'pt_BR'
    }
  });

  const onCreateTemplateSubmit = async (data: CreateTemplateFormData) => {
    if (!selectedWaba) {
      toast.error('Nenhuma WABA selecionada');
      return;
    }

    try {
      const payload = {
        name: data.name,
        category: data.category,
        language: data.language,
        components: [
          {
            type: 'BODY',
            text: data.body
          }
        ]
      };

      const response = await authenticatedFetch(`/api/meta/wabas/${selectedWaba.id}/templates`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Template criado com sucesso');
        setIsCreateTemplateModalOpen(false);
        resetCreateTemplate();
        loadMetaTemplates(selectedWaba.id);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao criar template');
      }
    } catch (error) {
      console.error('Erro ao criar template:', error);
      toast.error('Erro ao criar template');
    }
  };

  const loadGeneralSettings = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/settings');
      if (response.ok) {
        const data = await response.json();
        setGeneralSettings(data);
        setGeneralValue('companyName', data.companyName || '');
        setGeneralValue('pageTitle', data.pageTitle || '');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações gerais:', error);
      toast.error('Erro ao carregar configurações gerais');
    }
  }, [setGeneralValue]);

  const onGeneralSubmit = async (data: GeneralSettingsFormData) => {
    try {
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast.success('Configurações gerais salvas com sucesso');
        clearSettingsCache();
        loadGeneralSettings();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao salvar configurações gerais');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações gerais:', error);
      toast.error('Erro ao salvar configurações gerais');
    }
  };

  const loadIntegrationSettings = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/settings');
      if (response.ok) {
        const data = await response.json();
        setIntegrationSettings(data);
        setValue('wahaHost', data.wahaHost);
        setValue('wahaApiKey', data.wahaApiKey);
        setValue('evolutionHost', data.evolutionHost);
        setValue('evolutionApiKey', data.evolutionApiKey);
        setValue('evogoUrlBase', data.evogoUrlBase || '');
        setValue('evogoApiKeyGlobal', data.evogoApiKeyGlobal || '');
        setValue('webshareApiKey', data.webshareApiKey || '');
        setValue('webshareRotateDays', data.webshareRotateDays !== null && data.webshareRotateDays !== undefined ? data.webshareRotateDays : '');
        setValue('webshareRotateOnCampaign', !!data.webshareRotateOnCampaign);
        setValue('webshareRotateMessages', data.webshareRotateMessages !== null && data.webshareRotateMessages !== undefined ? data.webshareRotateMessages : '');
        setValue('quepasaUrl', data.quepasaUrl || '');
        setValue('quepasaLogin', data.quepasaLogin || '');
        setValue('quepasaPassword', data.quepasaPassword || '');
        setValue('whatsappAppId', data.whatsappAppId || '');
        setValue('whatsappAppSecret', data.whatsappAppSecret || '');
        setValue('whatsappConfigurationId', data.whatsappConfigurationId || '');
        setValue('whatsappApiVersion', data.whatsappApiVersion || '');
        setValue('whatsappSystemUserToken', data.whatsappSystemUserToken || '');
        setValue('whatsappBusinessId', data.whatsappBusinessId || '');
        setValue('whatsappWabaId', data.whatsappWabaId || '');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de integração:', error);
    }
  }, [setValue]);

  const handleTestConnection = async (service: 'waha' | 'evolution' | 'evogo' | 'quepasa' | 'webshare') => {
    const data = getValues();
    let config = {};

    if (service === 'waha') {
      if (!data.wahaHost) {
        toast.error('Informe o Host do WAHA para testar');
        return;
      }
      config = { host: data.wahaHost, apiKey: data.wahaApiKey };
    } else if (service === 'evolution') {
      if (!data.evolutionHost) {
        toast.error('Informe o Host do Evolution para testar');
        return;
      }
      config = { host: data.evolutionHost, apiKey: data.evolutionApiKey };
    } else if (service === 'evogo') {
      if (!data.evogoUrlBase) {
        toast.error('Informe a URL Base da Evogo para testar');
        return;
      }
      config = { urlBase: data.evogoUrlBase, apiKey: data.evogoApiKeyGlobal };
    } else if (service === 'quepasa') {
      if (!data.quepasaUrl || !data.quepasaLogin) {
        toast.error('Informe URL e Login do Quepasa para testar');
        return;
      }
      config = { url: data.quepasaUrl, login: data.quepasaLogin, password: data.quepasaPassword };
    } else if (service === 'webshare') {
      if (!data.webshareApiKey) {
        toast.error('Informe a API Key da Webshare para testar');
        return;
      }
      config = { apiKey: data.webshareApiKey };
    }

    const toastId = toast.loading('Testando conexão...');
    try {
      const response = await authenticatedFetch('/api/settings/test-connection', {
        method: 'POST',
        body: JSON.stringify({ service, config }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(result.message || 'Conexão estabelecida com sucesso!', { id: toastId });
      } else {
        toast.error(result.message || 'Falha na conexão', { id: toastId });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      toast.error('Erro ao testar conexão: ' + errorMessage, { id: toastId });
    }
  };

  const onIntegrationSubmit = async (data: SettingsFormData) => {
    try {
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast.success('Configurações de integração salvas com sucesso');
        clearSettingsCache();
        setActiveModal(null);
        loadIntegrationSettings();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao salvar configurações');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações de integração:', error);
      toast.error('Erro ao salvar configurações');
    }
  };

  // Logo handlers
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await authenticatedUpload('/api/settings/logo', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('Logo carregada com sucesso');
        clearSettingsCache();
        loadGeneralSettings();
        setLogoFile(null);
        setLogoPreview('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar logo');
      }
    } catch (error) {
      console.error('Erro ao carregar logo:', error);
      toast.error('Erro ao carregar logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    try {
      const response = await authenticatedFetch('/api/settings/logo', {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Logo removida com sucesso');
        clearSettingsCache();
        loadGeneralSettings();
      } else {
        toast.error('Erro ao remover logo');
      }
    } catch (error) {
      console.error('Erro ao remover logo:', error);
      toast.error('Erro ao remover logo');
    }
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFaviconFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setFaviconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadFavicon = async () => {
    if (!faviconFile) return;

    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('favicon', faviconFile);

      const response = await authenticatedUpload('/api/settings/favicon', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('Favicon carregado com sucesso');
        clearSettingsCache();
        loadGeneralSettings();
        setFaviconFile(null);
        setFaviconPreview('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar favicon');
      }
    } catch (error) {
      console.error('Erro ao carregar favicon:', error);
      toast.error('Erro ao carregar favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const loadMetaBusinesses = useCallback(async () => {
    setLoadingMeta(true);
    try {
      const response = await authenticatedFetch('/api/meta/businesses');
      if (response.ok) {
        const data = await response.json();
        setMetaBusinesses(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar Business Portfolios da Meta');
      }
    } catch (error) {
      console.error('Erro ao carregar Business Portfolios da Meta:', error);
      toast.error('Erro ao carregar Business Portfolios da Meta');
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  const loadMetaWabas = useCallback(async (businessId?: string) => {
    setLoadingMeta(true);
    try {
      const url = businessId ? `/api/meta/wabas?businessId=${businessId}` : '/api/meta/wabas';
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        // O backend agora retorna uma lista flat de WABAs
        const owned = Array.isArray(data) ? data : (data.owned || []);
        const client = Array.isArray(data) ? [] : (data.client || []);
        const all = Array.isArray(data) ? data : (data.all || []);
        
        setMetaWabas({ owned, client, all });
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar WABAs da Meta');
      }
    } catch (error) {
      console.error('Erro ao carregar WABAs da Meta:', error);
      toast.error('Erro ao carregar WABAs da Meta');
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  const loadMetaTemplates = useCallback(async (wabaId: string) => {
    setLoadingMeta(true);
    try {
      const response = await authenticatedFetch(`/api/meta/wabas/${wabaId}/templates`);
      if (!response.ok) {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar templates da Meta');
      }
    } catch (error) {
      console.error('Erro ao carregar templates da Meta:', error);
      toast.error('Erro ao carregar templates da Meta');
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  const loadWabaShares = useCallback(async (wabaId: string) => {
    try {
      const response = await authenticatedFetch(`/api/meta/wabas/${wabaId}/shares`);
      if (response.ok) {
        const data = await response.json();
        setWabaShares(data);
      }
    } catch (error) {
      console.error('Erro ao carregar compartilhamentos:', error);
    }
  }, []);

  const shareWabaWithTenant = async (wabaId: string, tenantId: string) => {
    try {
      const response = await authenticatedFetch(`/api/meta/wabas/${wabaId}/share`, {
        method: 'POST',
        body: JSON.stringify({ tenantId })
      });

      if (response.ok) {
        toast.success('WABA compartilhada com sucesso');
        loadWabaShares(wabaId);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao compartilhar WABA');
      }
    } catch (error) {
      console.error('Erro ao compartilhar WABA:', error);
      toast.error('Erro ao compartilhar WABA');
    }
  };

  const unshareWabaFromTenant = async (wabaId: string, tenantId: string) => {
    try {
      const response = await authenticatedFetch(`/api/meta/wabas/${wabaId}/share/${tenantId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Compartilhamento removido');
        loadWabaShares(wabaId);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao remover compartilhamento');
      }
    } catch (error) {
      console.error('Erro ao remover compartilhamento:', error);
      toast.error('Erro ao remover compartilhamento');
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'tenants') {
        const response = await authenticatedFetch('/api/tenants');
        if (response.ok) {
          const data = await response.json();
          setTenants(data.tenants || []);
        }

        // If in users sub-tab, also fetch users
        if (activeCompanySubTab === 'users') {
          const usersResponse = await authenticatedFetch('/api/users/global');
          if (usersResponse.ok) {
            const usersData = await usersResponse.json();
            setUsers(usersData.data?.users || []);
          }
        }
      } else if (activeTab === 'users') {
        // Usar endpoint global para SuperAdmin (visão de todos os usuários)
        const response = await authenticatedFetch('/api/users/global');
        if (response.ok) {
          const data = await response.json();
          setUsers(data.data?.users || []);
        }
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [activeTab, activeCompanySubTab]);

  useEffect(() => {
    loadData();
    if (activeTab === 'integrations' || activeTab === 'metaTemplates') {
      loadIntegrationSettings();
      if (activeTab === 'metaTemplates') {
        if (activeMetaSubTab === 'business') {
          loadMetaBusinesses();
        }
        if (activeMetaSubTab === 'wabas' || (activeMetaSubTab === 'business' && metaWabas.all.length === 0)) {
          loadMetaWabas(selectedBusinessId);
        }
      }
    }
    if (activeTab === 'general') {
      loadGeneralSettings();
    }
  }, [activeTab, activeCompanySubTab, activeMetaSubTab, selectedBusinessId, loadData, loadIntegrationSettings, loadGeneralSettings, loadMetaWabas, loadMetaBusinesses, metaWabas.all.length]);

  const handleToggleTenantStatus = async (tenant: Tenant) => {
    try {
      const response = await authenticatedFetch(`/api/tenants/${tenant.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...tenant,
          active: !tenant.active
        })
      });

      if (response.ok) {
        toast.success(`Empresa ${!tenant.active ? 'ativada' : 'desativada'} com sucesso!`);
        loadData();
      } else {
        throw new Error('Erro ao alterar status da empresa');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao alterar status da empresa');
    }
  };

  const handleCreateTenant = () => {
    setEditingTenant(null);
    setFormData({
      name: '',
      adminUser: {
        nome: '',
        email: '',
        senha: ''
      },
      quotas: {
        maxUsers: '10',
        maxContacts: '1000',
        maxCampaigns: '50',
        maxConnections: '5'
      },
      allowedProviders: [...DEFAULT_TENANT_PROVIDERS]
    });
    setIsModalOpen(true);
  };

  const handleEditTenant = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      adminUser: {
        nome: '',
        email: '',
        senha: ''
      },
      quotas: {
        maxUsers: tenant.quota?.maxUsers?.toString() || '10',
        maxContacts: tenant.quota?.maxContacts?.toString() || '1000',
        maxCampaigns: tenant.quota?.maxCampaigns?.toString() || '50',
        maxConnections: tenant.quota?.maxConnections?.toString() || '5'
      },
      allowedProviders: tenant.allowedProviders || [...DEFAULT_TENANT_PROVIDERS]
    });
    setIsModalOpen(true);
  };

  const handleDeleteTenant = async (tenant: Tenant) => {
    showConfirm(
      'Excluir Empresa',
      `Tem certeza que deseja excluir a empresa "${tenant.name}"? Esta ação é irreversível.`,
      async () => {
        try {
          const response = await authenticatedFetch(`/api/tenants/${tenant.id}`, {
            method: 'DELETE'
          });

          if (response.ok) {
            toast.success('Tenant excluído com sucesso!');
            loadData();
          } else {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao excluir empresa');
          }
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Erro ao excluir empresa');
        }
      }
    );
  };

  const handleSubmitTenant = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || (!editingTenant && (!formData.adminUser.nome || !formData.adminUser.email))) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (!formData.allowedProviders || formData.allowedProviders.length === 0) {
      toast.error('Selecione pelo menos um provedor permitido');
      return;
    }

    try {
      const url = editingTenant ? `/api/tenants/${editingTenant.id}` : '/api/tenants';
      const method = editingTenant ? 'PUT' : 'POST';

      const body: Record<string, unknown> = {
        name: formData.name,
        quotas: formData.quotas,
        allowedProviders: formData.allowedProviders
      };

      if (!editingTenant) {
        body.adminEmail = formData.adminUser.email;
        body.adminName = formData.adminUser.nome;
      }

      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(body)
      });

      if (response.ok) {
        await response.json();
        if (editingTenant) {
          toast.success('Empresa atualizada com sucesso!');
        } else {
          toast.success(`Empresa criada! Convite enviado para ${formData.adminUser.email}`);
        }
        setIsModalOpen(false);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || `Erro ao ${editingTenant ? 'atualizar' : 'criar'} empresa`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao processar empresa');
    }
  };

  const handleToggleAllowedProvider = (provider: string) => {
    setFormData((previous) => {
      const isSelected = previous.allowedProviders.includes(provider);
      const updatedProviders = isSelected
        ? previous.allowedProviders.filter((item) => item !== provider)
        : [...previous.allowedProviders, provider];
      return {
        ...previous,
        allowedProviders: TENANT_PROVIDER_OPTIONS
          .map((option) => option.value)
          .filter((value) => updatedProviders.includes(value))
      };
    });
  };

  const handleGenerateApiToken = async (tenant: Tenant) => {
    try {
      const response = await authenticatedFetch(`/api/tenants/${tenant.id}/generate-api-token`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedToken(data.token);
        setIsTokenDialogOpen(true);
        
        // Atualizar o tenant localmente
        setTenants(prevTenants => 
          prevTenants.map(t => 
            t.id === tenant.id ? { ...t, apiToken: data.token } : t
          )
        );
        
        // Se estiver editando este tenant, atualizar também
        if (editingTenant && editingTenant.id === tenant.id) {
          setEditingTenant({ ...editingTenant, apiToken: data.token });
        }
        
        toast.success('Token de API gerado com sucesso!');
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao gerar token de API');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao gerar token de API');
    }
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(generatedToken);
    toast.success('Token copiado para a área de transferência!');
  };

  // User management functions
  const loadAvailableTenants = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      console.log('🔍 Loading tenants, token exists:', !!token);

      const response = await authenticatedFetch('/api/users/tenants');

      console.log('🌐 Response status:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('📊 Tenants data:', data);
        setAvailableTenants(data.data?.tenants || []);
        console.log('✅ Available tenants set:', data.data?.tenants?.length || 0);
      } else {
        const errorData = await response.text();
        console.error('❌ Error response:', response.status, errorData);
      }
    } catch (error) {
      console.error('💥 Exception loading tenants:', error);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setTenantSearchQuery(''); // Limpar busca ao criar novo usuário
    setUserFormData({
      nome: '',
      email: '',
      senha: '',
      role: 'USER',
      tenantId: '',
      tenantIds: [],
      ativo: true
    });
    loadAvailableTenants();
    setIsUserModalOpen(true);
  };

  const handleEditUser = async (user: User) => {
    setEditingUser(user);
    setTenantSearchQuery(''); // Limpar busca ao editar usuário

    // Carregar tenants do usuário via novo endpoint
    let userTenantIds: string[] = [];
    try {
      const response = await authenticatedFetch(`/api/user-tenants/users/${user.id}/tenants`);

      if (response.ok) {
        const data = await response.json();
        console.log('📊 Tenants do usuário:', data);
        userTenantIds = (data.tenants || []).map((t: { id: string }) => t.id);
      }
    } catch (err) {
      console.error('Erro ao carregar tenants do usuário:', err);
    }

    setUserFormData({
      nome: user.nome,
      email: user.email,
      senha: '',
      role: user.role as 'SUPERADMIN' | 'ADMIN' | 'USER',
      tenantId: user.tenant?.id || '',
      tenantIds: userTenantIds,
      ativo: user.ativo
    });
    loadAvailableTenants();
    setIsUserModalOpen(true);
  };

  const handleSubmitUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userFormData.nome || !userFormData.email || (!editingUser && !userFormData.senha)) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const url = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
      const method = editingUser ? 'PUT' : 'POST';

      const body: Record<string, unknown> = {
        nome: userFormData.nome,
        email: userFormData.email,
        role: userFormData.role,
        ativo: userFormData.ativo
      };

      // Only send password if it's provided
      if (userFormData.senha) {
        body.senha = userFormData.senha;
      }

      // Handle tenant assignment based on role
      // Para SUPERADMIN, não enviamos tenantId (será null no backend)
      // Para outros roles, enviamos the first tenant selecionado como compatibilidade
      if (userFormData.role !== 'SUPERADMIN' && userFormData.tenantIds && userFormData.tenantIds.length > 0) {
        body.tenantId = userFormData.tenantIds[0];
      }

      const response = await authenticatedFetch(url, {
        method,
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const result = await response.json();
        const userId = editingUser ? editingUser.id : result.data?.user?.id;

        // Se tem tenants selecionados, criar associações (para qualquer role, incluindo SUPERADMIN)
        if (userFormData.tenantIds && userFormData.tenantIds.length > 0 && userId) {
          // Se estamos editando, primeiro remover associações antigas
          if (editingUser) {
            try {
              // Buscar associações existentes
              const existingAssociationsResponse = await authenticatedFetch(`/api/user-tenants`);

              if (existingAssociationsResponse.ok) {
                const existingData = await existingAssociationsResponse.json();
                // Remover associações que não estão mais selecionadas
                for (const association of existingData.tenants || []) {
                  if (!userFormData.tenantIds.includes(association.id)) {
                    try {
                      await authenticatedFetch(`/api/user-tenants/associations/${userId}/${association.id}`, {
                        method: 'DELETE'
                      });
                    } catch (err) {
                      console.warn('Erro ao remover associação:', association.id, err);
                    }
                  }
                }
              }
            } catch (err) {
              console.warn('Erro ao buscar/remover associações antigas:', err);
            }
          }

          // Criar associações para todos os tenants selecionados
          for (const tenantId of userFormData.tenantIds) {
            try {
              const associationResponse = await authenticatedFetch('/api/user-tenants/associations', {
                method: 'POST',
                body: JSON.stringify({
                  userId,
                  tenantId,
                  role: userFormData.role
                })
              });

              // Se der erro 400 (já existe), não é problema
              if (!associationResponse.ok && associationResponse.status !== 400) {
                console.warn('Erro ao criar associação para tenant:', tenantId);
              }
            } catch (err) {
              console.warn('Erro ao criar associação para tenant:', tenantId, err);
            }
          }
        }

        toast.success(`Usuário ${editingUser ? 'atualizado' : 'criado'} com sucesso!`);
        setIsUserModalOpen(false);
        loadData();
      } else {
        const error = await response.json();

        // Verificar se é erro de quota
        if (error.upgradeRequired || (error.message && error.message.includes('Limite'))) {
          toast.error(error.message || 'Limite de usuários atingido. Faça upgrade do plano para continuar.', {
            duration: 6000,
            icon: '⚠️'
          });
          return;
        }

        throw new Error(error.message || `Erro ao ${editingUser ? 'atualizar' : 'criar'} usuário`);
      }
    } catch (error) {
      // Não mostrar toast de erro genérico se já mostramos o toast específico de quota
      const errorMessage = error instanceof Error ? error.message : 'Erro ao processar usuário';
      if (!errorMessage.includes('Limite')) {
        toast.error(errorMessage);
      }
    }
  };

  const handleDeleteUser = async (user: User) => {
    showConfirm(
      'Excluir Usuário',
      `Tem certeza que deseja excluir o usuário ${user.nome}? Esta ação não pode ser desfeita.`,
      async () => {
        try {
          // Add ?global=true to force global delete (bypasses tenant context)
          const response = await authenticatedFetch(`/api/users/${user.id}?global=true`, {
            method: 'DELETE'
          });

          if (response.ok) {
            toast.success('Usuário excluído com sucesso!');
            loadData();
          } else {
            const error = await response.json();
            throw new Error(error.message || 'Erro ao excluir usuário');
          }
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Erro ao excluir usuário');
        }
      },
      'danger'
    );
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'Identidade', icon: '🎨', description: 'Personalização do sistema' },
    { id: 'integrations', label: 'Integrações', icon: '🔗', description: 'APIs externas' },
    { id: 'metaTemplates', label: 'Meta Templates', icon: '🧩', description: 'Business, WABA e templates' },
    { id: 'tenants', label: 'Empresas', icon: '🏢', description: 'Gestão de tenants' },
    { id: 'users', label: 'Usuários', icon: '👥', description: 'Gestão de acesso' },
    { id: 'backup', label: 'Backup', icon: '💾', description: 'Segurança de dados' },
    { id: 'email', label: 'Email Templates', icon: '📧', description: 'Templates globais' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] font-oswald overflow-x-hidden">
      <Header
        title="Super Admin"
        subtitle="Gerenciamento Global do Sistema"
        compact
        className="rounded-b-[2rem]"
        actions={
          <button
            onClick={loadData}
            className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-105 border border-blue-400/50 flex items-center gap-2 group/btn"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
            <RefreshCcw className="w-4 h-4" />
            Atualizar
          </button>
        }
      />

      <div className="p-4">
        <div className="max-w-7xl mx-auto space-y-4">

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Sidebar Navigation */}
          <div className="lg:w-80 flex flex-col gap-3">
            <div className="bg-white/5 backdrop-blur-md rounded-[2rem] border border-white/10 p-6 shadow-xl h-fit">
              <h3 className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] mb-6 px-2">
                Navegação Sistema
              </h3>
              <div className="flex flex-col gap-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as 'general' | 'integrations' | 'metaTemplates' | 'tenants' | 'users' | 'backup' | 'email')}
                    className={`w-full flex items-center gap-4 px-6 py-4 rounded-2xl transition-all duration-300 group relative overflow-hidden ${activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.3)]'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white'
                      }`}
                  >
                    <span className="relative z-10 text-xl">{tab.icon}</span>
                    <div className="relative z-10 flex flex-col items-start">
                      <span className="font-bold text-xs uppercase tracking-wider">{tab.label}</span>
                      <span className={`text-[9px] uppercase tracking-tight opacity-60 ${activeTab === tab.id ? 'text-blue-100' : 'text-gray-500'}`}>
                        {tab.description}
                      </span>
                    </div>
                    {activeTab === tab.id && (
                      <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent animate-pulse" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 space-y-4 min-w-0">
            {/* General Settings Tab */}
            {activeTab === 'general' && (
              <div className="space-y-4 animate-fadeIn">
                <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl">
                  <h2 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3 mb-8">
                    <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                    Identidade Visual
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    {/* Logo Upload Section */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 bg-blue-500/10 rounded-2xl border border-blue-500/20">
                          <Layout className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <h3 className="text-white font-bold uppercase tracking-wider text-sm">Logo do Sistema</h3>
                          <p className="text-gray-500 text-[10px] uppercase tracking-tight">Exibida no cabeçalho e login</p>
                        </div>
                      </div>

                      <div className="relative group/logo w-full aspect-video bg-white/5 rounded-3xl border-2 border-dashed border-white/10 flex flex-col items-center justify-center overflow-hidden transition-all hover:border-blue-500/30">
                        {logoPreview || generalSettings?.logoUrl ? (
                          <>
                            <img
                              src={logoPreview || generalSettings?.logoUrl}
                              alt="Logo Preview"
                              className="max-h-32 object-contain relative z-10"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/logo:opacity-100 transition-opacity flex items-center justify-center gap-4 z-20">
                              <button
                                onClick={removeLogo}
                                className="p-3 bg-red-500/20 text-red-400 rounded-2xl hover:bg-red-500 hover:text-white transition-all"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="text-center space-y-2">
                            <Plus className="w-8 h-8 text-gray-600 mx-auto" />
                            <p className="text-gray-500 text-[10px] uppercase font-bold">Clique para carregar</p>
                          </div>
                        )}
                        <input
                          type="file"
                          onChange={handleLogoChange}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          accept="image/png, image/jpeg, image/jpg, image/gif, image/webp, image/x-icon, image/svg+xml"
                        />
                      </div>

                      {logoFile && (
                        <button
                          onClick={uploadLogo}
                          disabled={uploadingLogo}
                          className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all disabled:opacity-50"
                        >
                          {uploadingLogo ? 'Carregando...' : 'Salvar Nova Logo'}
                        </button>
                      )}
                    </div>

                    {/* Text Settings Section */}
                    <form onSubmit={handleGeneralSubmit(onGeneralSubmit)} className="space-y-8">
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] ml-2">
                            Nome da Empresa
                          </label>
                          <input
                            {...registerGeneral('companyName')}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                            placeholder="Ex: BChat Soluções"
                          />
                          {generalErrors.companyName && (
                            <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{generalErrors.companyName.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] ml-2">
                            Título da Página (Browser)
                          </label>
                          <input
                            {...registerGeneral('pageTitle')}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                            placeholder="Ex: BChat | Painel Administrativo"
                          />
                          {generalErrors.pageTitle && (
                            <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{generalErrors.pageTitle.message}</p>
                          )}
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={isGeneralSubmitting}
                        className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all disabled:opacity-50 relative overflow-hidden group/save"
                      >
                        <div className="absolute inset-0 bg-white/10 translate-y-full group-hover/save:translate-y-0 transition-transform duration-300" />
                        <span className="relative z-10">{isGeneralSubmitting ? 'Salvando...' : 'Salvar Alterações'}</span>
                      </button>
                    </form>
                  </div>
                </div>

                {/* Favicon & Icon Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="p-3 bg-purple-500/10 rounded-2xl border border-purple-500/20">
                        <Globe className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold uppercase tracking-wider text-sm">Favicon</h3>
                        <p className="text-gray-500 text-[10px] uppercase tracking-tight">Ícone da aba do navegador</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-8">
                      <div className="w-20 h-20 bg-white/5 rounded-2xl border border-white/10 flex items-center justify-center overflow-hidden">
                        {(faviconPreview || generalSettings?.faviconUrl) ? (
                          <img src={faviconPreview || generalSettings?.faviconUrl} className="w-12 h-12 object-contain" alt="Favicon" />
                        ) : (
                          <Globe className="w-8 h-8 text-gray-700" />
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <input
                          type="file"
                          onChange={handleFaviconChange}
                          className="hidden"
                          id="favicon-upload"
                          accept="image/png, image/jpeg, image/jpg, image/gif, image/webp, image/x-icon, image/svg+xml"
                        />
                        <label
                          htmlFor="favicon-upload"
                          className="block w-full text-center py-3 bg-white/5 border border-white/10 text-white rounded-xl text-[10px] font-bold uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-all"
                        >
                          Selecionar Arquivo
                        </label>
                        {faviconFile && (
                          <button
                            onClick={uploadFavicon}
                            disabled={uploadingFavicon}
                            className="w-full py-3 bg-purple-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-wider hover:shadow-[0_0_15px_rgba(147,51,234,0.3)] transition-all disabled:opacity-50"
                          >
                            Upload Favicon
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="p-3 bg-orange-500/10 rounded-2xl border border-orange-500/20">
                        <Shield className="w-6 h-6 text-orange-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold uppercase tracking-wider text-sm">Ícone Mobile</h3>
                        <p className="text-gray-500 text-[10px] uppercase tracking-tight">App shortcut icon</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-8">
                      <div className="w-20 h-20 bg-white/5 rounded-2xl border border-white/10 flex items-center justify-center overflow-hidden">
                        {generalSettings?.iconUrl ? (
                          <img src={generalSettings?.iconUrl} className="w-12 h-12 object-contain" alt="Icon" />
                        ) : (
                          <Shield className="w-8 h-8 text-gray-700" />
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <input
                          type="file"
                          className="hidden"
                          id="icon-upload"
                          accept="image/png, image/jpeg, image/jpg, image/gif, image/webp, image/x-icon, image/svg+xml"
                        />
                        <label
                          htmlFor="icon-upload"
                          className="block w-full text-center py-3 bg-white/5 border border-white/10 text-white rounded-xl text-[10px] font-bold uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-all"
                        >
                          Selecionar Arquivo
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Integrations Tab */}
            {activeTab === 'integrations' && (
              <div className="space-y-8 animate-fadeIn">
                <div className="flex items-center gap-4 bg-white/5 p-2 rounded-2xl border border-white/10 w-fit">
                  <button
                    onClick={() => setActiveIntegrationSubTab('providers')}
                    className={`px-6 py-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeIntegrationSubTab === 'providers'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    Provedores
                  </button>
                  <button
                    onClick={() => setActiveIntegrationSubTab('webshare')}
                    className={`px-6 py-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeIntegrationSubTab === 'webshare'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    Webshare
                  </button>
                  <button
                    onClick={() => setActiveIntegrationSubTab('evocrm')}
                    className={`px-6 py-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeIntegrationSubTab === 'evocrm'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    EvoCRM
                  </button>
                </div>

                {activeIntegrationSubTab === 'providers' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
                    {/* WAHA Integration Card */}
                    <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Settings className="w-24 h-24 text-blue-400" />
                      </div>
                      <div className="relative z-10 space-y-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-600/20 rounded-2xl flex items-center justify-center border border-blue-500/30">
                            <Globe className="w-6 h-6 text-blue-400" />
                          </div>
                          <h3 className="text-xl font-bold text-white uppercase tracking-wider">WAHA API</h3>
                        </div>
                        <p className="text-gray-400 text-xs font-medium uppercase tracking-tighter leading-relaxed">
                          Configurações para integração com o provedor WAHA (WhatsApp HTTP API).
                        </p>
                        <button
                          onClick={() => setActiveModal('waha')}
                          className="w-full py-4 bg-blue-600/10 text-blue-400 border border-blue-500/20 rounded-2xl font-bold font-oswald text-[10px] uppercase tracking-[0.2em] hover:bg-blue-600 hover:text-white transition-all"
                        >
                          Configurar WAHA
                        </button>
                      </div>
                    </div>

                    {/* Evolution Integration Card */}
                    <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Settings className="w-24 h-24 text-emerald-400" />
                      </div>
                      <div className="relative z-10 space-y-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-emerald-600/20 rounded-2xl flex items-center justify-center border border-emerald-500/30">
                            <RefreshCcw className="w-6 h-6 text-emerald-400" />
                          </div>
                          <h3 className="text-xl font-bold text-white uppercase tracking-wider">Evolution</h3>
                        </div>
                        <p className="text-gray-400 text-xs font-medium uppercase tracking-tighter leading-relaxed">
                          Configurações para integração com o provedor Evolution API v2.
                        </p>
                        <button
                          onClick={() => setActiveModal('evolution')}
                          className="w-full py-4 bg-emerald-600/10 text-emerald-400 border border-emerald-500/20 rounded-2xl font-bold font-oswald text-[10px] uppercase tracking-[0.2em] hover:bg-emerald-600 hover:text-white transition-all"
                        >
                          Configurar Evolution
                        </button>
                      </div>
                    </div>

                    {/* Evogo Integration Card */}
                    <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Globe className="w-24 h-24 text-orange-400" />
                      </div>
                      <div className="relative z-10 space-y-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-orange-600/20 rounded-2xl flex items-center justify-center border border-orange-500/30">
                            <Globe className="w-6 h-6 text-orange-400" />
                          </div>
                          <h3 className="text-xl font-bold text-white uppercase tracking-wider">Evogo</h3>
                        </div>
                        <p className="text-gray-400 text-xs font-medium uppercase tracking-tighter leading-relaxed">
                          Configurações para integração com o provedor WhatsApp Evogo.
                        </p>
                        <button
                          onClick={() => setActiveModal('evogo')}
                          className="w-full py-4 bg-orange-600/10 text-orange-400 border border-orange-500/20 rounded-2xl font-bold font-oswald text-[10px] uppercase tracking-[0.2em] hover:bg-orange-600 hover:text-white transition-all"
                        >
                          Configurar Evogo
                        </button>
                      </div>
                    </div>

                    {/* Quepasa Integration Card */}
                    <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Settings className="w-24 h-24 text-amber-400" />
                      </div>
                      <div className="relative z-10 space-y-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-amber-600/20 rounded-2xl flex items-center justify-center border border-amber-500/30">
                            <Database className="w-6 h-6 text-amber-400" />
                          </div>
                          <h3 className="text-xl font-bold text-white uppercase tracking-wider">Quepasa</h3>
                        </div>
                        <p className="text-gray-400 text-xs font-medium uppercase tracking-tighter leading-relaxed">
                          Configurações para integração com o provedor Quepasa (v2 Legacy).
                        </p>
                        <button
                          onClick={() => setActiveModal('quepasa')}
                          className="w-full py-4 bg-amber-600/10 text-amber-400 border border-amber-500/20 rounded-2xl font-bold font-oswald text-[10px] uppercase tracking-[0.2em] hover:bg-amber-600 hover:text-white transition-all"
                        >
                          Configurar Quepasa
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeIntegrationSubTab === 'webshare' && (
                  <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl">
                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-white uppercase tracking-wider">Webshare Proxy Rotativo</h3>
                      <p className="text-gray-400 text-xs uppercase tracking-wider mt-2">
                        Configure a API Key global da Webshare para aplicação automática de proxy nas instâncias Evogo.
                      </p>
                    </div>

                    <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-6">
                      <div>
                        <label htmlFor="webshareApiKey" className="block text-sm font-medium text-gray-200 mb-2">
                          API Key
                        </label>
                        <input
                          id="webshareApiKey"
                          type="password"
                          {...register('webshareApiKey')}
                          placeholder="Token da Webshare"
                          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                        {errors.webshareApiKey && (
                          <p className="text-red-400 text-xs mt-2">{errors.webshareApiKey.message}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <div className="space-y-2">
                          <label className="text-xs font-bold font-oswald uppercase tracking-widest text-gray-400 ml-1 flex items-center gap-2">
                            <span>Rotacionar a cada (em dias)</span>
                          </label>
                          <input
                            {...register('webshareRotateDays')}
                            type="number"
                            min="1"
                            placeholder="Ex: 7"
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs font-bold font-oswald uppercase tracking-widest text-gray-400 ml-1 flex items-center gap-2">
                            <span>Número de mensagens enviadas</span>
                          </label>
                          <input
                            {...register('webshareRotateMessages')}
                            type="number"
                            min="1"
                            placeholder="Ex: 500"
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mt-6 p-4 bg-white/5 border border-white/10 rounded-2xl">
                        <input
                          {...register('webshareRotateOnCampaign')}
                          type="checkbox"
                          id="webshareRotateOnCampaign"
                          className="w-5 h-5 rounded border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-900 bg-white/5 cursor-pointer"
                        />
                        <label htmlFor="webshareRotateOnCampaign" className="text-sm font-bold font-oswald uppercase tracking-widest text-gray-300 cursor-pointer">
                          Rotacionar a cada nova campanha
                        </label>
                      </div>

                      <div className="flex gap-3 mt-6">
                        <button
                          type="button"
                          onClick={() => handleTestConnection('webshare')}
                          className="px-5 py-3 text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-blue-500/20 transition-all"
                        >
                          Testar Conexão
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="px-5 py-3 bg-blue-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-blue-700 disabled:opacity-50 transition-all"
                        >
                          {isSubmitting ? 'Salvando...' : 'Salvar'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {activeIntegrationSubTab === 'evocrm' && (
                  <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl">
                    <h3 className="text-xl font-bold text-white uppercase tracking-wider mb-3">EvoCRM</h3>
                    <p className="text-gray-400 text-sm">
                      Subaba criada. Configurações do EvoCRM podem ser adicionadas nesta seção conforme evolução do produto.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'metaTemplates' && (
              <div className="space-y-8 animate-fadeIn">
                <div className="flex items-center gap-4 bg-white/5 p-2 rounded-2xl border border-white/10 w-fit">
                  <button
                    onClick={() => setActiveMetaSubTab('business')}
                    className={`px-6 py-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeMetaSubTab === 'business'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    Business Portfolio
                  </button>
                  <button
                    onClick={() => setActiveMetaSubTab('wabas')}
                    className={`px-6 py-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeMetaSubTab === 'wabas'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    WABAs
                  </button>
                  <button
                    onClick={() => setActiveMetaSubTab('templates')}
                    className={`px-6 py-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeMetaSubTab === 'templates'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    Templates
                  </button>
                  <button
                    onClick={() => setActiveMetaSubTab('settings')}
                    className={`px-6 py-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeMetaSubTab === 'settings'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    Settings
                  </button>
                </div>

                {activeMetaSubTab === 'business' && (
                  <div className="space-y-8 animate-fadeIn">
                    <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl">
                      <div className="flex justify-between items-center mb-8">
                        <div>
                          <h2 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                            <span className="w-2 h-6 bg-indigo-500 rounded-full shadow-[0_0_15px_rgba(99,102,241,0.5)]"></span>
                            Business Portfolios
                          </h2>
                          <p className="text-gray-400 text-sm uppercase tracking-wider mt-1">
                            Selecione um Business Account para gerenciar suas conexões.
                          </p>
                        </div>
                        <button 
                          onClick={loadMetaBusinesses}
                          disabled={loadingMeta}
                          className="p-3 bg-white/5 border border-white/10 rounded-2xl text-gray-400 hover:text-white hover:bg-white/10 transition-all disabled:opacity-50"
                        >
                          <RefreshCcw className={`w-5 h-5 ${loadingMeta ? 'animate-spin' : ''}`} />
                        </button>
                      </div>

                      {loadingMeta && metaBusinesses.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 space-y-4">
                          <div className="w-12 h-12 border-4 border-indigo-600/20 border-t-indigo-600 rounded-full animate-spin"></div>
                          <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">Buscando portfólios na Meta...</p>
                        </div>
                      ) : metaBusinesses.length === 0 ? (
                        <div className="text-center py-20 bg-white/5 rounded-3xl border-2 border-dashed border-white/10">
                          <Building2 className="w-16 h-16 text-gray-700 mx-auto mb-6 opacity-20" />
                          <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Nenhum Business Portfolio encontrado</p>
                          <p className="text-gray-600 text-[9px] mt-2 max-w-xs mx-auto">Verifique se o token de usuário do sistema tem a permissão business_management.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {metaBusinesses.map((biz) => (
                            <div
                              key={biz.id}
                              onClick={() => {
                                setSelectedBusinessId(biz.id);
                                loadMetaWabas(biz.id);
                                setActiveMetaSubTab('wabas');
                              }}
                              className={`group p-6 rounded-3xl border transition-all cursor-pointer relative overflow-hidden ${selectedBusinessId === biz.id
                                ? 'bg-indigo-600/20 border-indigo-500/50 shadow-[0_0_30px_rgba(99,102,241,0.2)]'
                                : 'bg-white/5 border-white/10 hover:border-indigo-500/30 hover:bg-white/[0.08]'
                                }`}
                            >
                              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                                <Building2 className="w-16 h-16 text-indigo-400" />
                              </div>
                              <div className="relative z-10">
                                <div className="flex items-center gap-4 mb-4">
                                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border transition-all ${selectedBusinessId === biz.id
                                    ? 'bg-indigo-600 border-indigo-400'
                                    : 'bg-white/10 border-white/10 group-hover:bg-indigo-600/50'
                                    }`}>
                                    <Building2 className="w-6 h-6 text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-bold text-white uppercase tracking-tight line-clamp-1">{biz.name}</h4>
                                    <p className="text-gray-500 font-mono text-[9px] mt-1 tracking-widest">ID: {biz.id}</p>
                                  </div>
                                </div>
                                <div className={`flex items-center justify-between text-[9px] font-bold uppercase tracking-widest transition-all ${selectedBusinessId === biz.id ? 'text-indigo-400' : 'text-gray-500 group-hover:text-indigo-400'
                                  }`}>
                                  <span>Gerenciar WABAs</span>
                                  <Layout className="w-4 h-4" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-blue-500/10 rounded-xl">
                              <Building2 className="w-5 h-5 text-blue-400" />
                            </div>
                            <p className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em]">Owned WABAs</p>
                          </div>
                          <p className="text-3xl font-bold text-white tracking-tight">{metaWabas.owned.length}</p>
                        </div>

                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-purple-500/10 rounded-xl">
                              <Shield className="w-5 h-5 text-purple-400" />
                            </div>
                            <p className="text-[10px] font-bold text-purple-400 uppercase tracking-[0.2em]">Shared WABAs</p>
                          </div>
                          <p className="text-3xl font-bold text-white tracking-tight">{metaWabas.client.length}</p>
                        </div>

                        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-emerald-500/10 rounded-xl">
                              <CheckCircle className="w-5 h-5 text-emerald-400" />
                            </div>
                            <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em]">Status API</p>
                          </div>
                          <p className="text-xl font-bold text-emerald-400 tracking-tight uppercase">Conectado</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeMetaSubTab === 'wabas' && (
                  <div className="space-y-8 animate-fadeIn">
                    <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl">
                      <div className="flex justify-between items-center mb-8">
                        <div>
                          <h2 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                            <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                            WhatsApp Business Accounts
                            {selectedBusinessId && (
                              <span className="text-sm font-normal text-gray-500 lowercase tracking-normal ml-2">
                                em {metaBusinesses.find(b => b.id === selectedBusinessId)?.name || selectedBusinessId}
                              </span>
                            )}
                          </h2>
                          <p className="text-gray-400 text-sm uppercase tracking-wider mt-1">
                            Selecione uma conta para gerenciar seus templates.
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {selectedBusinessId && (
                            <button 
                              onClick={() => {
                                setSelectedBusinessId('');
                                loadMetaWabas();
                              }}
                              className="px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-[10px] font-bold text-gray-400 hover:text-white uppercase tracking-wider transition-all"
                            >
                              Limpar Filtro
                            </button>
                          )}
                          <button 
                            onClick={() => loadMetaWabas(selectedBusinessId)}
                            disabled={loadingMeta}
                            className="p-3 bg-white/5 border border-white/10 rounded-2xl text-gray-400 hover:text-white hover:bg-white/10 transition-all disabled:opacity-50"
                          >
                            <RefreshCcw className={`w-5 h-5 ${loadingMeta ? 'animate-spin' : ''}`} />
                          </button>
                        </div>
                      </div>

                      {loadingMeta && metaWabas.all.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 space-y-4">
                          <div className="w-12 h-12 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
                          <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">Buscando contas na Meta...</p>
                        </div>
                      ) : metaWabas.all.length === 0 ? (
                        <div className="text-center py-20 bg-white/5 rounded-[2rem] border border-dashed border-white/10">
                          <Building2 className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                          <p className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">Nenhuma WABA encontrada</p>
                          <p className="text-gray-600 text-[9px] uppercase mt-2">Verifique suas credenciais nas configurações</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-4">
                          {metaWabas.all.map((waba) => (
                            <div 
                              key={waba.id}
                              className={`group relative bg-white/5 border border-white/10 rounded-3xl p-6 transition-all hover:bg-white/10 cursor-pointer ${selectedWaba?.id === waba.id ? 'ring-2 ring-blue-600 bg-white/10' : ''}`}
                              onClick={() => {
                                setSelectedWaba(waba);
                                setActiveMetaSubTab('templates');
                                loadMetaTemplates(waba.id);
                              }}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-4">
                                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${metaWabas.owned.find(o => o.id === waba.id) ? 'bg-blue-600/10 border-blue-500/30' : 'bg-purple-600/10 border-purple-500/30'}`}>
                                    <Globe className={`w-6 h-6 ${metaWabas.owned.find(o => o.id === waba.id) ? 'text-blue-400' : 'text-purple-400'}`} />
                                  </div>
                                  <div>
                                    <h4 className="text-white font-bold uppercase tracking-wider text-sm group-hover:text-blue-400 transition-colors">{waba.name}</h4>
                                    <div className="flex items-center gap-3 mt-1">
                                      <span className="text-[9px] font-mono text-gray-500 uppercase">ID: {waba.id}</span>
                                      <span className="w-1 h-1 bg-gray-700 rounded-full"></span>
                                      <span className={`text-[9px] font-bold uppercase tracking-widest ${waba.status === 'APPROVED' ? 'text-emerald-400' : 'text-amber-400'}`}>{waba.status}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="text-right mr-4 hidden md:block">
                                    <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">Tipo</p>
                                    <p className="text-[10px] font-bold text-white uppercase mt-1">
                                      {metaWabas.owned.find(o => o.id === waba.id) ? 'Proprietário' : 'Compartilhada'}
                                    </p>
                                  </div>
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedWabaToShare(waba);
                                      setIsShareModalOpen(true);
                                      loadWabaShares(waba.id);
                                    }}
                                    className="p-3 bg-white/5 rounded-xl text-gray-500 hover:text-white hover:bg-emerald-600 transition-all"
                                    title="Compartilhar com Tenantes"
                                  >
                                    <Share2 className="w-5 h-5" />
                                  </button>
                                  <button className="p-3 bg-white/5 rounded-xl text-gray-500 group-hover:text-white group-hover:bg-blue-600 transition-all">
                                    <Layout className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeMetaSubTab === 'templates' && (
                  <div className="animate-fadeIn">
                    <MetaTemplatesManager isSuperAdmin={true} />
                  </div>
                )}

                {/* Modal Criar Template */}
                {isCreateTemplateModalOpen && (
                  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-[#121212] border border-white/10 rounded-[2.5rem] w-full max-w-2xl overflow-hidden shadow-2xl animate-scaleIn">
                      <div className="p-8 border-b border-white/5 bg-white/5">
                        <div className="flex justify-between items-center">
                          <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                            <span className="w-2 h-6 bg-pink-600 rounded-full shadow-[0_0_15px_rgba(236,72,153,0.5)]"></span>
                            Novo Template Meta
                          </h3>
                          <button 
                            onClick={() => setIsCreateTemplateModalOpen(false)}
                            className="p-2 text-gray-500 hover:text-white transition-colors"
                          >
                            <Plus className="w-6 h-6 rotate-45" />
                          </button>
                        </div>
                      </div>

                      <form onSubmit={handleCreateTemplateSubmit(onCreateTemplateSubmit)} className="p-8 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-pink-400 uppercase tracking-[0.2em] ml-2">Nome do Template</label>
                            <input
                              {...registerCreateTemplate('name')}
                              className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-pink-500/50 transition-all"
                              placeholder="ex: boas_vindas_v1"
                            />
                            {createTemplateErrors.name && (
                              <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{createTemplateErrors.name.message}</p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-pink-400 uppercase tracking-[0.2em] ml-2">Categoria</label>
                            <select
                              {...registerCreateTemplate('category')}
                              className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:outline-none focus:ring-2 focus:ring-pink-500/50 transition-all appearance-none"
                            >
                              <option value="MARKETING" className="bg-[#121212]">MARKETING</option>
                              <option value="UTILITY" className="bg-[#121212]">UTILITY</option>
                              <option value="AUTHENTICATION" className="bg-[#121212]">AUTHENTICATION</option>
                            </select>
                            {createTemplateErrors.category && (
                              <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{createTemplateErrors.category.message}</p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <label className="text-[10px] font-bold text-pink-400 uppercase tracking-[0.2em] ml-2">Idioma</label>
                            <input
                              {...registerCreateTemplate('language')}
                              className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-pink-500/50 transition-all"
                              placeholder="ex: pt_BR"
                            />
                            {createTemplateErrors.language && (
                              <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{createTemplateErrors.language.message}</p>
                            )}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-pink-400 uppercase tracking-[0.2em] ml-2">Corpo da Mensagem (BODY)</label>
                          <textarea
                            {...registerCreateTemplate('body')}
                            rows={4}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-pink-500/50 transition-all resize-none"
                            placeholder="Digite o conteúdo da mensagem..."
                          />
                          <p className="text-[9px] text-gray-500 uppercase tracking-wider ml-2">Use {'{{1}}'}, {'{{2}}'} para variáveis</p>
                          {createTemplateErrors.body && (
                            <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{createTemplateErrors.body.message}</p>
                          )}
                        </div>

                        <div className="flex gap-4 pt-4">
                          <button
                            type="button"
                            onClick={() => setIsCreateTemplateModalOpen(false)}
                            className="flex-1 py-4 bg-white/5 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:bg-white/10 transition-all"
                          >
                            Cancelar
                          </button>
                          <button
                            type="submit"
                            disabled={isCreatingTemplate}
                            className="flex-2 px-12 py-4 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(236,72,153,0.3)] transition-all disabled:opacity-50"
                          >
                            {isCreatingTemplate ? 'Criando...' : 'Criar Template'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {isShareModalOpen && (
                  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-[#121212] border border-white/10 rounded-[2.5rem] w-full max-w-2xl overflow-hidden shadow-2xl animate-scaleIn">
                      <div className="p-8 border-b border-white/5 bg-white/5 flex justify-between items-center">
                        <div>
                          <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                            <span className="w-2 h-6 bg-emerald-600 rounded-full shadow-[0_0_15px_rgba(16,185,129,0.5)]"></span>
                            Compartilhar WABA
                          </h3>
                          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mt-2 ml-5">{selectedWabaToShare?.name}</p>
                        </div>
                        <button 
                          onClick={() => setIsShareModalOpen(false)}
                          className="p-2 text-gray-500 hover:text-white transition-colors"
                        >
                          <Plus className="w-6 h-6 rotate-45" />
                        </button>
                      </div>
                      
                      <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
                        <div className="space-y-4">
                          <h4 className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] ml-2">Tenantes com Acesso</h4>
                          <div className="space-y-2">
                            {wabaShares.length === 0 ? (
                              <p className="text-gray-600 text-[10px] font-bold uppercase italic py-4 text-center">Nenhum tenante associado ainda</p>
                            ) : (
                              wabaShares.map((share) => (
                                <div key={share.id} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-emerald-600/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold text-xs">
                                      {share.tenant.name.substring(0, 2).toUpperCase()}
                                    </div>
                                    <span className="text-white text-sm font-bold uppercase tracking-wider">{share.tenant.name}</span>
                                  </div>
                                  <button 
                                    onClick={() => unshareWabaFromTenant(selectedWabaToShare.id, share.tenant.id)}
                                    className="p-2 hover:bg-red-600/20 text-red-400 rounded-xl transition-all"
                                    title="Remover Acesso"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] ml-2">Adicionar Tenante</h4>
                          <div className="grid grid-cols-1 gap-2">
                            {tenants
                              .filter(t => !wabaShares.some(s => s.tenantId === t.id))
                              .map((tenant) => (
                                <button 
                                  key={tenant.id}
                                  onClick={() => shareWabaWithTenant(selectedWabaToShare.id, tenant.id)}
                                  className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 rounded-2xl border border-white/10 transition-all text-left group"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 group-hover:text-white font-bold text-xs transition-colors">
                                      {tenant.name.substring(0, 2).toUpperCase()}
                                    </div>
                                    <span className="text-gray-400 group-hover:text-white text-sm font-bold uppercase tracking-wider transition-colors">{tenant.name}</span>
                                  </div>
                                  <Plus className="w-4 h-4 text-gray-600 group-hover:text-emerald-400 transition-colors" />
                                </button>
                              ))
                            }
                            {tenants.filter(t => !wabaShares.some(s => s.tenantId === t.id)).length === 0 && (
                              <p className="text-gray-600 text-[10px] font-bold uppercase italic py-4 text-center">Todos os tenantes já possuem acesso</p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="p-8 bg-white/5 flex justify-end">
                        <button 
                          onClick={() => setIsShareModalOpen(false)}
                          className="px-12 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all"
                        >
                          Fechar
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {activeMetaSubTab === 'settings' && (
                  <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl">
                    <h2 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3 mb-8">
                      <span className="w-2 h-6 bg-emerald-500 rounded-full shadow-[0_0_15px_rgba(16,185,129,0.5)]"></span>
                      Settings Meta WhatsApp
                    </h2>

                    <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] ml-2">WhatsApp App ID</label>
                          <input
                            {...register('whatsappAppId')}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                            placeholder="Ex: 123456789012345"
                          />
                          <p className="text-gray-500 text-[9px] uppercase tracking-wider ml-2">Obtido no Dashboard do App na Meta</p>
                          {errors.whatsappAppId && (
                            <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.whatsappAppId.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] ml-2">WhatsApp App Secret</label>
                          <input
                            type="password"
                            {...register('whatsappAppSecret')}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                            placeholder="••••••••••••••••"
                          />
                          <p className="text-gray-500 text-[9px] uppercase tracking-wider ml-2">Obtido nas Configurações Básicas do App</p>
                          {errors.whatsappAppSecret && (
                            <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.whatsappAppSecret.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] ml-2">WhatsApp Configuration ID</label>
                          <input
                            {...register('whatsappConfigurationId')}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                            placeholder="Ex: 987654321098765"
                          />
                          <p className="text-gray-500 text-[9px] uppercase tracking-wider ml-2">ID da configuração de WABA</p>
                          {errors.whatsappConfigurationId && (
                            <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.whatsappConfigurationId.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] ml-2">WhatsApp API Version</label>
                          <input
                            {...register('whatsappApiVersion')}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                            placeholder="Ex: v19.0"
                          />
                          <p className="text-gray-500 text-[9px] uppercase tracking-wider ml-2">Ex: v19.0 (Versão mais recente da API Graph)</p>
                          {errors.whatsappApiVersion && (
                            <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.whatsappApiVersion.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] ml-2">WhatsApp System User Token</label>
                          <input
                            type="password"
                            {...register('whatsappSystemUserToken')}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                            placeholder="EAAG... (Token de acesso permanente)"
                          />
                          <p className="text-gray-500 text-[9px] uppercase tracking-wider ml-2">Gerado no Business Manager → System Users</p>
                          {errors.whatsappSystemUserToken && (
                            <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.whatsappSystemUserToken.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] ml-2">WhatsApp Business ID</label>
                          <input
                            {...register('whatsappBusinessId')}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                            placeholder="Ex: 112233445566778"
                          />
                          <p className="text-gray-500 text-[9px] uppercase tracking-wider ml-2">ID do Portfólio de Negócios (Meta Business Suite)</p>
                          {errors.whatsappBusinessId && (
                            <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.whatsappBusinessId.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] ml-2">WhatsApp WABA ID</label>
                          <input
                            {...register('whatsappWabaId')}
                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                            placeholder="Ex: 998877665544332"
                          />
                          <p className="text-gray-500 text-[9px] uppercase tracking-wider ml-2">ID da Conta de WhatsApp Business (WABA)</p>
                          {errors.whatsappWabaId && (
                            <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.whatsappWabaId.message}</p>
                          )}
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all disabled:opacity-50 relative overflow-hidden group/save"
                      >
                        <div className="absolute inset-0 bg-white/10 translate-y-full group-hover/save:translate-y-0 transition-transform duration-300" />
                        <span className="relative z-10">{isSubmitting ? 'Salvando...' : 'Salvar Configurações Meta'}</span>
                      </button>
                    </form>
                  </div>
                )}
              </div>
            )}

            {/* Tenants (Empresas) Tab */}
            {activeTab === 'tenants' && (
              <div className="space-y-8 animate-fadeIn">
                {/* Sub-tabs Navigation */}
                <div className="flex items-center gap-4 bg-white/5 p-2 rounded-2xl border border-white/10 w-fit">
                  <button
                    onClick={() => setActiveCompanySubTab('dashboard')}
                    className={`px-6 py-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeCompanySubTab === 'dashboard'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => setActiveCompanySubTab('list')}
                    className={`px-6 py-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeCompanySubTab === 'list'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    Empresas
                  </button>
                  <button
                    onClick={() => setActiveCompanySubTab('users')}
                    className={`px-6 py-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all ${activeCompanySubTab === 'users'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                  >
                    Usuários
                  </button>
                </div>

                {/* Dashboard Sub-tab */}
                {activeCompanySubTab === 'dashboard' && (
                  <div className="space-y-5 animate-fade-in">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* Gerenciar Empresas - Left */}
                      <div className="flex-1 flex justify-between items-center bg-white/5 p-8 rounded-[2.5rem] border border-white/10 backdrop-blur-md shadow-2xl">
                        <div>
                          <h2 className="text-2xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                            <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                            Gerenciar Empresas
                          </h2>
                          <p className="text-gray-400 text-sm font-medium mt-1 uppercase tracking-wider opacity-70">Visão geral e monitoramento de tenants</p>
                        </div>
                        <button
                          onClick={handleCreateTenant}
                          className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-105 relative overflow-hidden group/btn border border-blue-400/50 flex items-center gap-2"
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                          <Plus className="w-5 h-5" />
                          Nova Empresa
                        </button>
                      </div>

                      {/* Stats Cards - Right */}
                      <div className="flex gap-6">
                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 shadow-2xl hover:bg-white/10 transition-all group relative overflow-hidden min-w-[180px]">
                          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                            <Building2 className="w-16 h-16 text-blue-400" />
                          </div>
                          <div className="flex flex-col gap-1">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">EMPRESAS</p>
                            <p className="text-4xl font-bold text-white tracking-tight">{tenants.length}</p>
                          </div>
                        </div>
                        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 shadow-2xl hover:bg-white/10 transition-all group relative overflow-hidden min-w-[180px]">
                          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                            <CheckCircle className="w-16 h-16 text-green-400" />
                          </div>
                          <div className="flex flex-col gap-1">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">ATIVAS</p>
                            <p className="text-4xl font-bold text-green-400 tracking-tight">{tenants.filter(t => t.active).length}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Companies Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {tenants.map((tenant) => (
                        <div key={tenant.id} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] p-8 hover:shadow-2xl hover:bg-white/10 transition-all group relative overflow-hidden">
                          <div className="flex justify-between items-start mb-8">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-blue-600/10 rounded-2xl border border-blue-500/20 flex items-center justify-center text-blue-400">
                                <Building2 className="w-6 h-6" />
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-white uppercase tracking-tight group-hover:text-blue-400 transition-colors">{tenant.name}</h3>
                                <p className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.2em] mt-1">{tenant.slug}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleToggleTenantStatus(tenant)}
                              className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border shadow-sm transition-all ${tenant.active
                                ? 'bg-green-500/10 text-green-400 border-green-500/20 shadow-green-900/20 hover:bg-green-500 hover:text-white'
                                : 'bg-red-500/10 text-red-400 border-red-500/20 shadow-red-900/20 hover:bg-red-500 hover:text-white'
                                }`}
                            >
                              {tenant.active ? 'Ativo' : 'Inativo'}
                            </button>
                          </div>

                          {tenant.quota && (
                            <div className="space-y-6 mb-10">
                              {/* Quota Indicators */}
                              <div className="space-y-2">
                                <div className="flex justify-between items-end">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                    Usuários
                                  </span>
                                  <span className="text-xs font-bold text-white">{tenant._count?.users || 0} / {tenant.quota.maxUsers}</span>
                                </div>
                                <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="h-full bg-blue-600 rounded-full transition-all duration-500"
                                    style={{ width: `${Math.min(((tenant._count?.users || 0) / tenant.quota.maxUsers) * 100, 100)}%` }}
                                  />
                                </div>
                              </div>

                              <div className="space-y-2">
                                <div className="flex justify-between items-end">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                    Conexões
                                  </span>
                                  <span className="text-xs font-bold text-white">{tenant._count?.whatsappSessions || 0} / {tenant.quota.maxConnections}</span>
                                </div>
                                <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className="h-full bg-indigo-600 rounded-full transition-all duration-500"
                                    style={{ width: `${Math.min(((tenant._count?.whatsappSessions || 0) / tenant.quota.maxConnections) * 100, 100)}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center gap-3 pt-6 border-t border-white/5">
                            <button
                              onClick={() => handleEditTenant(tenant)}
                              className="flex-1 py-3 bg-white/5 hover:bg-blue-600 text-gray-400 hover:text-white rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all border border-white/5 hover:border-blue-500"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleDeleteTenant(tenant)}
                              className="p-3 bg-white/5 hover:bg-red-600 text-gray-400 hover:text-white rounded-xl transition-all border border-white/5 hover:border-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* List Sub-tab */}
                {activeCompanySubTab === 'list' && (
                  <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl overflow-hidden">
                    <div className="flex justify-between items-center mb-8">
                      <h3 className="text-xl font-bold text-white uppercase tracking-tight">Lista de Empresas</h3>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Buscar empresa..."
                          value={tenantSearchQuery}
                          onChange={(e) => setTenantSearchQuery(e.target.value)}
                          className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                        />
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Nome</th>
                            <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Slug</th>
                            <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</th>
                            <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Usuários</th>
                            <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider text-right">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {tenants
                            .filter(t => t.name.toLowerCase().includes(tenantSearchQuery.toLowerCase()))
                            .map((tenant) => (
                              <tr key={tenant.id} className="group hover:bg-white/5 transition-all">
                                <td className="py-4 text-sm font-medium text-white">{tenant.name}</td>
                                <td className="py-4 text-xs text-gray-400">{tenant.slug}</td>
                                <td className="py-4">
                                  <span className={`inline-flex px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-tighter ${tenant.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                    }`}>
                                    {tenant.active ? 'Ativo' : 'Inativo'}
                                  </span>
                                </td>
                                <td className="py-4 text-xs text-gray-400">
                                  {tenant._count?.users || 0} / {tenant.quota?.maxUsers || 0}
                                </td>
                                <td className="py-4 text-right">
                                  <div className="flex justify-end gap-2">
                                    <button onClick={() => handleGenerateApiToken(tenant)} className="p-2 text-gray-400 hover:text-yellow-400 transition-colors" title="Gerar Token de API">
                                      <Key className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleEditTenant(tenant)} className="p-2 text-gray-400 hover:text-blue-400 transition-colors">
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => handleDeleteTenant(tenant)} className="p-2 text-gray-400 hover:text-red-400 transition-colors">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Users Sub-tab */}
                {activeCompanySubTab === 'users' && (
                  <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl overflow-hidden">
                    <div className="flex justify-between items-center mb-8">
                      <h3 className="text-xl font-bold text-white uppercase tracking-tight">Usuários do Sistema</h3>
                      <button
                        onClick={handleCreateUser}
                        className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold text-[10px] uppercase tracking-wider hover:bg-blue-700 transition-all flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" /> Novo Usuário
                      </button>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Nome</th>
                            <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Email</th>
                            <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Empresa</th>
                            <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Cargo</th>
                            <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider text-right">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {users.map((user) => (
                            <tr key={user.id} className="group hover:bg-white/5 transition-all">
                              <td className="py-4 text-sm font-medium text-white">{user.nome}</td>
                              <td className="py-4 text-xs text-gray-400">{user.email}</td>
                              <td className="py-4 text-xs text-gray-400">{user.tenant?.name || 'Sistema'}</td>
                              <td className="py-4">
                                <span className={`inline-flex px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-tighter ${user.role === 'SUPERADMIN' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'
                                  }`}>
                                  {user.role}
                                </span>
                              </td>
                              <td className="py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button onClick={() => handleEditUser(user)} className="p-2 text-gray-400 hover:text-blue-400 transition-colors">
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => handleDeleteUser(user)} className="p-2 text-gray-400 hover:text-red-400 transition-colors">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Users Tab (Main) */}
            {activeTab === 'users' && (
              <div className="space-y-8 animate-fadeIn">
                <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 shadow-2xl">
                  <div className="flex justify-between items-center mb-8">
                    <h3 className="text-xl font-bold text-white uppercase tracking-tight">Gerenciar Usuários Globais</h3>
                    <button
                      onClick={handleCreateUser}
                      className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold text-[10px] uppercase tracking-wider hover:bg-blue-700 transition-all flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" /> Novo Usuário
                    </button>
                  </div>
                  {/* Reuse the users table logic here or reference a component */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Nome</th>
                          <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Email</th>
                          <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Empresa</th>
                          <th className="pb-4 text-[10px] font-bold text-gray-400 uppercase tracking-wider text-right">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {users.map((user) => (
                          <tr key={user.id} className="group hover:bg-white/5 transition-all">
                            <td className="py-4 text-sm font-medium text-white">{user.nome}</td>
                            <td className="py-4 text-xs text-gray-400">{user.email}</td>
                            <td className="py-4 text-xs text-gray-400">{user.tenant?.name || 'Sistema'}</td>
                            <td className="py-4 text-right">
                              <div className="flex justify-end gap-2">
                                <button onClick={() => handleEditUser(user)} className="p-2 text-gray-400 hover:text-blue-400 transition-colors">
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteUser(user)} className="p-2 text-gray-400 hover:text-red-400 transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Backup Tab */}
            {activeTab === 'backup' && (
              <SystemBackup />
            )}

            {/* Email Templates Tab */}
            {activeTab === 'email' && (
              <EmailTemplatesManager isGlobal={true} />
            )}

            {/* Modal WAHA */}
            {activeModal === 'waha' && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">🔗 Configurar WAHA</h3>
                    <button
                      onClick={() => setActiveModal(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>

                  <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-4">
                    <div>
                      <label htmlFor="wahaHost" className="block text-sm font-medium text-gray-700 mb-1">
                        Host WAHA *
                      </label>
                      <input
                        id="wahaHost"
                        type="url"
                        {...register('wahaHost')}
                        placeholder="https://waha.exemplo.com.br"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {errors.wahaHost && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.wahaHost.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="wahaApiKey" className="block text-sm font-medium text-gray-700 mb-1">
                        API Key WAHA *
                      </label>
                      <input
                        id="wahaApiKey"
                        type="password"
                        {...register('wahaApiKey')}
                        placeholder="sua-api-key-aqui"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {errors.wahaApiKey && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.wahaApiKey.message}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => handleTestConnection('waha')}
                        className="flex-1 px-4 py-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                      >
                        Testar Conexão
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveModal(null)}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Modal Evolution API */}
            {activeModal === 'evolution' && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">🚀 Configurar Evolution API</h3>
                    <button
                      onClick={() => setActiveModal(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>

                  <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-4">
                    <div>
                      <label htmlFor="evolutionHost" className="block text-sm font-medium text-gray-700 mb-1">
                        Host Evolution *
                      </label>
                      <input
                        id="evolutionHost"
                        type="url"
                        {...register('evolutionHost')}
                        placeholder="https://evolution.exemplo.com.br"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {errors.evolutionHost && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.evolutionHost.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="evolutionApiKey" className="block text-sm font-medium text-gray-700 mb-1">
                        API Key Evolution *
                      </label>
                      <input
                        id="evolutionApiKey"
                        type="password"
                        {...register('evolutionApiKey')}
                        placeholder="sua-api-key-evolution-aqui"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {errors.evolutionApiKey && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.evolutionApiKey.message}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => handleTestConnection('evolution')}
                        className="flex-1 px-4 py-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                      >
                        Testar Conexão
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveModal(null)}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Modal Quepasa */}
            {activeModal === 'evogo' && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">🟠 Configurar Evogo</h3>
                    <button
                      onClick={() => setActiveModal(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>

                  <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-4">
                    <div>
                      <label htmlFor="evogoUrlBase" className="block text-sm font-medium text-gray-700 mb-1">
                        URL Base Evogo *
                      </label>
                      <input
                        id="evogoUrlBase"
                        type="url"
                        {...register('evogoUrlBase')}
                        placeholder="https://evogo.exemplo.com.br"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      {errors.evogoUrlBase && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.evogoUrlBase.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="evogoApiKeyGlobal" className="block text-sm font-medium text-gray-700 mb-1">
                        API Key Global *
                      </label>
                      <input
                        id="evogoApiKeyGlobal"
                        type="password"
                        {...register('evogoApiKeyGlobal')}
                        placeholder="sua-api-key-global-evogo"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      {errors.evogoApiKeyGlobal && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.evogoApiKeyGlobal.message}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => handleTestConnection('evogo')}
                        className="flex-1 px-4 py-2 text-orange-600 bg-orange-50 border border-orange-200 rounded-md hover:bg-orange-100"
                      >
                        Testar Conexão
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveModal(null)}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50"
                      >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Modal Quepasa */}
            {activeModal === 'quepasa' && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                      <img src="/logoquepasa.png?v=2" alt="Quepasa" className="w-6 h-6 object-contain" />
                      <h3 className="text-lg font-semibold text-gray-900">Configurar Quepasa</h3>
                    </div>
                    <button
                      onClick={() => setActiveModal(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>

                  <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-4">
                    <div>
                      <label htmlFor="quepasaUrl" className="block text-sm font-medium text-gray-700 mb-1">
                        URL do Quepasa *
                      </label>
                      <input
                        id="quepasaUrl"
                        type="url"
                        {...register('quepasaUrl')}
                        placeholder="https://quepasa.exemplo.com.br"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      {errors.quepasaUrl && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.quepasaUrl.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="quepasaLogin" className="block text-sm font-medium text-gray-700 mb-1">
                        Login *
                      </label>
                      <input
                        id="quepasaLogin"
                        type="text"
                        {...register('quepasaLogin')}
                        placeholder="admin"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      {errors.quepasaLogin && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.quepasaLogin.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="quepasaPassword" className="block text-sm font-medium text-gray-700 mb-1">
                        Senha *
                      </label>
                      <input
                        id="quepasaPassword"
                        type="password"
                        {...register('quepasaPassword')}
                        placeholder="••••••••••••••••"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      {errors.quepasaPassword && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.quepasaPassword.message}
                        </p>
                      )}
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => handleTestConnection('quepasa')}
                        className="flex-1 px-4 py-2 text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100"
                      >
                        Testar Conexão
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveModal(null)}
                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                      >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Empresa */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto backdrop-blur-xl relative flex flex-col custom-scrollbar">
            <div className="p-10 border-b border-white/10 bg-white/5 backdrop-blur-md sticky top-0 z-10">
              <h2 className="text-3xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-4">
                <span className="p-3 bg-blue-600/10 rounded-[1.25rem] border border-blue-500/20 text-2xl shadow-[0_0_20px_rgba(37,99,235,0.2)]">🏢</span>
                {editingTenant ? 'Editar Empresa' : 'Nova Empresa'}
              </h2>
            </div>

            <form onSubmit={handleSubmitTenant} className="p-10 space-y-10">
              <div className="space-y-8">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                  <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                  Informações Básicas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-8">
                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Nome da Empresa</label>
                    <input
                      type="text"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                      placeholder="Ex: Minha Empresa"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                {editingTenant && (
                  <div className="space-y-3 pt-6 border-t border-white/5">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Token de API</label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        className="flex-1 bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner font-mono text-xs"
                        placeholder="Nenhum token gerado"
                        value={editingTenant.apiToken || ''}
                        readOnly
                      />
                      <button
                        type="button"
                        onClick={() => handleGenerateApiToken(editingTenant)}
                        className="px-6 py-4 bg-yellow-600/10 text-yellow-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl border border-yellow-500/30 hover:bg-yellow-600/20 transition-all font-oswald flex items-center gap-2"
                      >
                        <Key className="w-4 h-4" />
                        Gerar
                      </button>
                    </div>
                    <p className="text-[10px] text-gray-500 font-medium">
                      Use este token para autenticar chamadas de API externas. O token pode ser regenerado a qualquer momento.
                    </p>
                  </div>
                )}
              </div>

              {!editingTenant && (
                <div className="space-y-8 pt-10 border-t border-white/5">
                  <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                    <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                    Administrador do Sistema
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Nome Completo</label>
                      <input
                        type="text"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                        value={formData.adminUser.nome}
                        onChange={e => setFormData({ ...formData, adminUser: { ...formData.adminUser, nome: e.target.value } })}
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Email Principal</label>
                      <input
                        type="email"
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                        value={formData.adminUser.email}
                        onChange={e => setFormData({ ...formData, adminUser: { ...formData.adminUser, email: e.target.value } })}
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-8 pt-10 border-t border-white/5">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                  <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                  Limites e Quotas
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="space-y-3">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Usuários</label>
                    <input
                      type="number"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                      value={formData.quotas.maxUsers}
                      onChange={e => setFormData({ ...formData, quotas: { ...formData.quotas, maxUsers: e.target.value } })}
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Contatos</label>
                    <input
                      type="number"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                      value={formData.quotas.maxContacts}
                      onChange={e => setFormData({ ...formData, quotas: { ...formData.quotas, maxContacts: e.target.value } })}
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Campanhas</label>
                    <input
                      type="number"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                      value={formData.quotas.maxCampaigns}
                      onChange={e => setFormData({ ...formData, quotas: { ...formData.quotas, maxCampaigns: e.target.value } })}
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Conexões</label>
                    <input
                      type="number"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                      value={formData.quotas.maxConnections}
                      onChange={e => setFormData({ ...formData, quotas: { ...formData.quotas, maxConnections: e.target.value } })}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-8 pt-10 border-t border-white/5">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                  <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                  Provedores Permitidos
                </h3>
                <p className="text-[11px] text-gray-400 font-medium">
                  Selecione quais provedores este tenant poderá usar para criar sessões de WhatsApp.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {TENANT_PROVIDER_OPTIONS.map((provider) => {
                    const isSelected = formData.allowedProviders.includes(provider.value);

                    return (
                      <button
                        key={provider.value}
                        type="button"
                        onClick={() => handleToggleAllowedProvider(provider.value)}
                        className={`w-full rounded-2xl border p-4 transition-all text-left ${
                          isSelected
                            ? 'bg-blue-600/10 border-blue-500/50 shadow-[0_0_20px_rgba(37,99,235,0.2)]'
                            : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <img src={provider.icon} alt={provider.label} className="w-8 h-8 object-contain" />
                            <div className="min-w-0">
                              <p className={`text-xs font-bold uppercase tracking-[0.14em] font-oswald ${isSelected ? 'text-blue-300' : 'text-white'}`}>
                                {provider.label}
                              </p>
                              <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-[0.1em] truncate">
                                {provider.description}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`w-6 h-6 rounded-full border flex items-center justify-center transition-all ${
                              isSelected
                                ? 'bg-blue-500 border-blue-400 text-white'
                                : 'border-white/25 text-transparent'
                            }`}
                          >
                            <Check className="w-3.5 h-3.5" />
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {formData.allowedProviders.length === 0 && (
                  <p className="text-red-400 text-xs font-semibold uppercase tracking-wider">
                    Selecione pelo menos um provedor para continuar.
                  </p>
                )}
              </div>

              <div className="flex gap-6 pt-10 border-t border-white/5 sticky bottom-0 bg-gradient-to-t from-[#0f172a] to-transparent">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all font-oswald"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-10 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:bg-blue-700 transition-all hover:scale-[1.02] active:scale-[0.98] border border-blue-500 relative overflow-hidden group/btn font-oswald"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  {editingTenant ? 'Salvar Alterações' : 'Criar Empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Usuário */}
      {isUserModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto backdrop-blur-xl relative flex flex-col custom-scrollbar">
            <div className="p-10 border-b border-white/10 bg-white/5 backdrop-blur-md sticky top-0 z-10">
              <h2 className="text-3xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-4">
                <span className="p-3 bg-blue-600/10 rounded-[1.25rem] border border-blue-500/20 text-2xl shadow-[0_0_20px_rgba(37,99,235,0.2)]">👤</span>
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </h2>
            </div>

            <form onSubmit={handleSubmitUser} className="p-10 space-y-10">
              <div className="space-y-8">
                <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                  <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                  Informações Pessoais
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Nome Completo</label>
                    <input
                      type="text"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                      value={userFormData.nome}
                      onChange={e => setUserFormData({ ...userFormData, nome: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Email</label>
                    <input
                      type="email"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                      value={userFormData.email}
                      onChange={e => setUserFormData({ ...userFormData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">
                      {editingUser ? 'Nova Senha (deixe em branco para não alterar)' : 'Senha'}
                    </label>
                    <input
                      type="password"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white placeholder-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                      value={userFormData.senha}
                      onChange={e => setUserFormData({ ...userFormData, senha: e.target.value })}
                      required={!editingUser}
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Cargo / Role</label>
                    <select
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 outline-none transition-all shadow-inner"
                      value={userFormData.role}
                      onChange={e => setUserFormData({ ...userFormData, role: e.target.value as 'SUPERADMIN' | 'ADMIN' | 'USER' })}
                    >
                      <option value="USER" className="bg-[#1e293b]">Usuário Padrão</option>
                      <option value="ADMIN" className="bg-[#1e293b]">Administrador</option>
                      <option value="SUPERADMIN" className="bg-[#1e293b]">Super Admin</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="space-y-8 pt-10 border-t border-white/5">
                <div className="flex justify-between items-center">
                  <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.3em] font-oswald flex items-center gap-3">
                    <span className="w-2 h-5 bg-blue-600 rounded-full shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                    Tenants Associados
                  </h3>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Buscar tenants..."
                      className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white outline-none focus:border-blue-500 transition-all"
                      value={tenantSearchQuery}
                      onChange={e => setTenantSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                  {availableTenants
                    .filter(t => t.name.toLowerCase().includes(tenantSearchQuery.toLowerCase()))
                    .map(tenant => (
                    <label 
                      key={tenant.id}
                      className={`flex items-center gap-4 p-4 rounded-2xl border transition-all cursor-pointer ${
                        userFormData.tenantIds?.includes(tenant.id)
                          ? 'bg-blue-600/10 border-blue-500/50 text-white'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={userFormData.tenantIds?.includes(tenant.id)}
                        onChange={() => {
                          const currentIds = userFormData.tenantIds || [];
                          if (currentIds.includes(tenant.id)) {
                            setUserFormData({ ...userFormData, tenantIds: currentIds.filter(id => id !== tenant.id) });
                          } else {
                            setUserFormData({ ...userFormData, tenantIds: [...currentIds, tenant.id] });
                          }
                        }}
                      />
                      <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                        userFormData.tenantIds?.includes(tenant.id)
                          ? 'bg-blue-600 border-blue-500'
                          : 'border-white/20'
                      }`}>
                        {userFormData.tenantIds?.includes(tenant.id) && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm font-bold font-oswald uppercase tracking-wider">{tenant.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-6 pt-10 border-t border-white/5 sticky bottom-0 bg-gradient-to-t from-[#0f172a] to-transparent">
                <button
                  type="button"
                  onClick={() => setIsUserModalOpen(false)}
                  className="flex-1 px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all font-oswald"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-10 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-lg shadow-blue-900/40 hover:bg-blue-700 transition-all hover:scale-[1.02] active:scale-[0.98] border border-blue-500 relative overflow-hidden group/btn font-oswald"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog 
        open={confirmDialog.isOpen} 
        onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}
      >
        <AlertDialogContent className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border-white/10 rounded-[2.5rem] p-10 backdrop-blur-3xl shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-3xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-4">
              <div className={`p-3 rounded-[1.25rem] border ${
                confirmDialog.type === 'danger' 
                  ? 'bg-red-500/10 border-red-500/20 text-red-500 shadow-[0_0_20px_rgba(239,68,68,0.2)]' 
                  : 'bg-blue-500/10 border-blue-500/20 text-blue-500 shadow-[0_0_20px_rgba(37,99,235,0.2)]'
              }`}>
                {confirmDialog.type === 'danger' ? (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              {confirmDialog.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400 text-base font-medium leading-relaxed pt-4 pl-1">
              {confirmDialog.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl border-white/5 hover:bg-white/10 hover:border-white/10 transition-all font-oswald border">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog.onConfirm}
              className={`px-10 py-4 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl transition-all duration-300 flex items-center gap-3 border relative overflow-hidden group/btn font-oswald ${
                confirmDialog.type === 'danger'
                  ? 'bg-red-600 border-red-500 shadow-lg shadow-red-900/40 hover:bg-red-700'
                  : 'bg-blue-600 border-blue-500 shadow-lg shadow-blue-900/40 hover:bg-blue-700'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Token Dialog */}
      {isTokenDialogOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-white/10 rounded-[2.5rem] shadow-2xl max-w-2xl w-full backdrop-blur-xl relative">
            <div className="p-10 border-b border-white/10 bg-white/5 backdrop-blur-md">
              <h2 className="text-3xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-4">
                <span className="p-3 bg-yellow-600/10 rounded-[1.25rem] border border-yellow-500/20 text-2xl shadow-[0_0_20px_rgba(234,179,8,0.2)]">🔑</span>
                Token de API Gerado
              </h2>
            </div>

            <div className="p-10 space-y-8">
              <div className="space-y-4">
                <p className="text-gray-400 text-sm font-medium leading-relaxed">
                  O token de API foi gerado com sucesso! Copie o token abaixo e use-o para autenticar chamadas de API externas.
                </p>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Token de API</label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      className="flex-1 bg-black/30 border border-white/10 rounded-xl p-4 text-white font-mono text-xs break-all"
                      value={generatedToken}
                      readOnly
                    />
                    <button
                      onClick={handleCopyToken}
                      className="px-6 py-4 bg-blue-600 text-white font-bold uppercase tracking-[0.2em] text-[10px] rounded-xl border border-blue-500 hover:bg-blue-700 transition-all font-oswald flex items-center gap-2 whitespace-nowrap"
                    >
                      <Copy className="w-4 h-4" />
                      Copiar
                    </button>
                  </div>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 space-y-2">
                  <p className="text-yellow-400 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Importante
                  </p>
                  <p className="text-gray-400 text-xs font-medium leading-relaxed">
                    Guarde este token em local seguro. Ele não será exibido novamente. Se perder o token, você precisará gerar um novo.
                  </p>
                </div>
              </div>

              <div className="flex gap-6 pt-6 border-t border-white/5">
                <button
                  onClick={() => setIsTokenDialogOpen(false)}
                  className="flex-1 px-8 py-4 bg-white/5 text-gray-400 font-bold uppercase tracking-[0.2em] text-[10px] rounded-2xl border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all font-oswald"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
